import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/lib/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl shadow-lg shadow-navy-900/5 border-b border-slate-100"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="container-site flex h-[4.5rem] items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="Oz SpotNest home" data-testid="header-logo-link">
          <img
            src="/images/logo.webp"
            alt="Oz SpotNest Cleaning Service logo"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-slate-100"
            width="44"
            height="44"
          />
          <span className="leading-tight">
            <span className="block font-heading text-lg font-bold text-navy-800 tracking-tight">
              Oz <span className="text-sky-600">Spot</span>Nest
            </span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.28em] text-emerald-600">
              Cleaning Service
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-3.5 py-2 text-sm font-semibold font-heading transition-colors duration-300 ${
                  isActive ? "text-emerald-600 bg-emerald-50" : "text-navy-800/75 hover:text-navy-800 hover:bg-slate-50"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-phone-link"
            className="hidden xl:flex items-center gap-2 text-sm font-bold font-heading text-navy-800 hover:text-emerald-600 transition-colors"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <Phone className="h-4 w-4" />
            </span>
            {BUSINESS.phone}
          </a>
          <Link to="/contact" data-testid="header-quote-button" className="btn-primary hidden sm:inline-flex !px-6 !py-2.5 text-sm">
            Get a Free Quote
          </Link>
          <button
            type="button"
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-navy-800"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            aria-label="Mobile navigation"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-slate-100 bg-white/95 backdrop-blur-xl"
          >
            <div className="container-site flex flex-col gap-1 py-6 pb-24">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                >
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      `block rounded-xl px-4 py-3 font-heading text-lg font-semibold ${
                        isActive ? "bg-emerald-50 text-emerald-600" : "text-navy-800 hover:bg-slate-50"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-4 flex flex-col gap-3"
              >
                <Link to="/contact" data-testid="mobile-menu-quote-button" className="btn-primary w-full">
                  Get a Free Quote
                </Link>
                <a href={BUSINESS.phoneHref} data-testid="mobile-menu-call-button" className="btn-outline w-full">
                  <Phone className="h-4 w-4" /> Call {BUSINESS.phone}
                </a>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
